"use client";

import React, { useEffect, useState } from "react";
import { useNonogramContext } from "./nonogram_context";
import { Button } from "../../../../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from "../../../../components/ui/dialog";
import { Card, CardContent, CardFooter } from "../../../../components/ui/card";
import NonogramGenerator from "./nonogram_generator";
import { toast } from "sonner";
import { get } from "../../../../lib/api";
import { Edit, Trash2 } from "lucide-react";
import LoadingScreenDenda from "../../Animation/loading_screen";
import { NonogramViewer } from "../Generator From Image/nonogram_viewer";

const ExistingNonogramViewer = ({ game_id }) => {
  // State to store nonograms categorized by their status
  const [nonograms, setNonograms] = useState([]);
  // Destructure functions and data from Nonogram context
  const { newUploadedNono } = useNonogramContext();

  // State variables to control dialog visibility
  const [currentNonogram, setCurrentNonogram] = useState(null); // State to hold the currently selected nonogram
  const [changeNonogram, setChangeNonogram] = useState(false); // Trigger for updating nonogram data
  const [method, setMethod] = useState(""); // Method to determine the type of action to perform
  const [isLoading, setIsLoading] = useState(true); // State to control loading spinner
  const [showViewer, setShowViewer] = useState(false); // State to control the visibility of the nonogram viewer

  // Function to delete a nonogram challenge
  async function deleteChallenge(name, game_id) {
    await fetch(process.env.REACT_APP_CMC_API + "/statistics", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        method: "deleteChallenge",
        nonogram: name,
        gameid: game_id,
      }),
    });
  }

  async function replaceOutdatedChallenges(game_id) {
    await fetch(process.env.REACT_APP_CMC_API + "/statistics", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        method: "replaceOutdatedChallenges",
        gameid: game_id,
      }),
    });
  }


  // useEffect hook to fetch nonogram data when game_id changes
  const fetchNonograms = async () => {
    if (game_id === null) return;

    try {
      const url = `/statistics?method=getDailyChallengeNonogram&gameid=${game_id}`;
      const response = await get(url);

      // Convert response to array if it's a single object
      const dataArray = Array.isArray(response) ? response : [response];

      // Map the data directly to the format we need
      const formattedData = dataArray.map(({ name, bw, color, date }) => ({
        name,
        data1: bw,
        data2: color,
        date,
      }));

      setNonograms(formattedData);
      setIsLoading(false);
    } catch (error) {
      toast.error("Failed to fetch nonograms");
      console.error("Error fetching nonograms:", error);
    }
  };

  // Update the drawNonogram and drawColorNonogram functions to use dynamic sizing
  const calculateCanvasSize = (dataLength) => {
    const gridSize = Math.sqrt(dataLength);
    const minSize = 100; // Minimum canvas size
    const maxSize = 200; // Maximum canvas size
    const cellSize = Math.floor(minSize / gridSize);
    return Math.min(gridSize * cellSize, maxSize);
  };

  const drawNonogram = (canvas, nonogramData) => {
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const size = Math.sqrt(nonogramData.length);
    const canvasSize = calculateCanvasSize(nonogramData.length);
    const cellSize = canvasSize / size;

    canvas.width = canvasSize;
    canvas.height = canvasSize;

    // Fill background
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, canvasSize, canvasSize);

    nonogramData.forEach((cell, index) => {
      const x = index % size;
      const y = Math.floor(index / size);
      ctx.fillStyle = cell === "1" ? "black" : "white";
      ctx.fillRect(x * cellSize, y * cellSize, cellSize, cellSize);
    });
  };

  const drawColorNonogram = (canvas, rgbData) => {
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const totalPixels = Math.floor(rgbData.length / 3);
    const gridSize = Math.round(Math.sqrt(totalPixels));
    const canvasSize = calculateCanvasSize(totalPixels);
    const cellSize = canvasSize / gridSize;

    canvas.width = canvasSize;
    canvas.height = canvasSize;

    for (let i = 0; i < gridSize * gridSize; i++) {
      const x = i % gridSize;
      const y = Math.floor(i / gridSize);
      const index = i * 3;
      const r = rgbData[index];
      const g = rgbData[index + 1];
      const b = rgbData[index + 2];
      ctx.fillStyle = `rgb(${r},${g},${b})`;
      ctx.fillRect(x * cellSize, y * cellSize, cellSize, cellSize);
    }
  };

  // useEffect hook to handle new uploaded nonograms
  useEffect(() => {
    if (game_id === null || newUploadedNono.gameid !== game_id) return;

    setNonograms((prevNonograms) => [
      ...prevNonograms,
      {
        name: newUploadedNono.name,
        date: newUploadedNono.newDate,
        data1: newUploadedNono.data.bw,
        data2: newUploadedNono.data.color,
      },
    ]);
  }, [newUploadedNono, game_id]);

  useEffect(() => {
    fetchNonograms();
  }, [game_id]);

  // Function to remove a nonogram from the list after deletion
  const deleteNonogramLive = () => {
    if (!currentNonogram) return;

    setNonograms((prevNonograms) => prevNonograms.filter((nonogram) => nonogram.name !== currentNonogram.name));
  };

  // useEffect hook to handle nonogram changes based on 'method' and 'changeNonogram' state
  useEffect(() => {
    if (game_id === null || !changeNonogram) return;
    const changeChallengePost = async () => {
      const name = currentNonogram.name;
      switch (method) {
        case "delete": {
          await deleteChallenge(name, game_id);
          await replaceOutdatedChallenges(game_id)
          toast.success(`Successfully deleted ${name}!`);
          deleteNonogramLive();
          break;
        }
        default:
          console.warn(`Unhandled method: ${method}`);
      }
    };
    changeChallengePost();
    setChangeNonogram(false); // Reset the trigger
  }, [changeNonogram]);

  // Function to handle selecting a nonogram for editing
  const selectNonogram = async (nonogram) => {
    setCurrentNonogram(nonogram);
  };

  // Function to handle confirmation of deletion
  const handleDeleteConfirmation = async () => {
    // Close the delete confirmation dialog
    // Proceed with deletion
    setMethod("delete");
    setChangeNonogram(true); // Trigger the change
  };

  // Function to initiate the deletion process (opens confirmation dialog)
  const deleteNonogram = (nonogram) => {
    setCurrentNonogram(nonogram);
  };

  if (isLoading) {
    return <LoadingScreenDenda />;
  }

  // Render the component UI
  return (
    <div className="space-y-4 ">
      <NonogramViewer showViewer={showViewer} setShowViewer={setShowViewer} />
      {/* Display the list of nonograms */}
      <div className="overflow-y-auto max-h-[500px]">
        {nonograms.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-2">
            {Array.isArray(nonograms) &&
              nonograms.map((nonogram, index) => (
                <Card key={`${nonogram.name}-${index}`} className="overflow-hidden dark:bg-[var(--header-color-dark)] space-y-2.5 max-w-[230px] border-1 border-white/40">
                  <CardContent className="p-0 w-full">
                    <div className="relative aspect-square w-full">
                      <canvas
                        ref={(canvas) => {
                          if (canvas && nonogram.data1) {
                            drawNonogram(canvas, nonogram?.data1?.split(","));
                          }
                        }}
                        className="absolute inset-0 w-full h-full object-contain"
                      />
                      <canvas
                        ref={(canvas) => {
                          if (canvas && nonogram.data2) {
                            drawColorNonogram(canvas, nonogram?.data2?.split(",").map(Number));
                          }
                        }}
                        className="absolute inset-0 w-full h-full object-contain opacity-50"
                      />
                    </div>
                    <div className="space-y-1 text-center">
                      <p className="text-center text-lg font-bold"> {nonogram.name} </p>

                      <p className="text-xs text-muted-foreground !pt-0 !pb-0 !pr-0 !pl-0">
                        Size: {Math.sqrt(nonogram?.data1?.split(",").length)}x{Math.sqrt(nonogram?.data1?.split(",").length)}
                      </p>
                      <p className="text-xs text-muted-foreground !pt-0 !pr-0 !pl-0">Date: {new Date(nonogram.date).toLocaleDateString()}</p>
                    </div>
                  </CardContent>
                  <CardFooter className="flex justify-center gap-2">
                    {/* Edit Nonogram Dialog */}
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button
                          variant="outline"
                          onClick={() => {
                            selectNonogram(nonogram);
                            setShowViewer(true);
                          }}
                        >
                          <Edit size={16} />
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Edit Nonogram</DialogTitle>
                        </DialogHeader>
                        <DialogContent
                          className="min-w-fit dark:bg-[var(--header-color-dark)] border-0"
                          onInteractOutside={(e) => {
                            e.preventDefault();
                          }}
                        >
                          {currentNonogram && (
                            <NonogramGenerator
                              game_id={game_id}
                              initialNonogramDataBW={currentNonogram.data1} // black & white data
                              initialNonogramDataColor={currentNonogram.data2} // color data
                              initialChallengeName={currentNonogram.name}
                              onUpdate={() => {
                                fetchNonograms(); // Refresh the nonogram list
                              }}
                              showViewer={showViewer}
                              setShowViewer={setShowViewer}
                            />
                          )}
                        </DialogContent>
                      </DialogContent>
                    </Dialog>

                    {/* Delete Confirmation Dialog */}
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="destructive" className={"bg-red-600"} onClick={() => deleteNonogram(nonogram)}>
                          <Trash2 size={16} />
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Confirm Deletion</DialogTitle>
                          <DialogDescription>Are you sure you want to delete this nonogram? This action cannot be undone.</DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                          <DialogClose asChild>
                            <Button variant="outline">Cancel</Button>
                          </DialogClose>
                          <Button variant="destructive" onClick={handleDeleteConfirmation}>
                            Delete
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </CardFooter>
                </Card>
              ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">No nonograms available</div>
        )}
      </div>
    </div>
  );
};

export default ExistingNonogramViewer;
